import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { musicEndpoints } from "../utilities/musicEndpoints";
import {
  IMusicSuggestion,
  IMusicSuggestionData,
  ISpotifyTrack,
  IUpvoteResponse,
} from "../models/musicSuggestionModels";
import { getSuccessToast, getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";

interface IMusicSuggestionsResponse {
  suggestions: IMusicSuggestionData[];
  eventEndDateTime: string;
  allowMusicSuggestions: boolean;
}

interface ISpotifyTracksResponse {
  tracks: ISpotifyTrack[];
}

export const useMusicSuggestions = (eventId: number) => {
  const queryClient = useQueryClient();
  const deleteMutation = useApiMutation();
  const upvoteMutation = useApiMutation();

  // Fetch suggestions (DB data only - TrackIds)
  const {
    data: suggestionsResponse,
    isLoading: isLoadingSuggestions,
    error: suggestionsError,
  } = useApiQuery<IMusicSuggestionsResponse>({
    queryKey: ["musicSuggestions", eventId],
    endpoint: musicEndpoints.getSuggestions(eventId),
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  const suggestions = suggestionsResponse?.suggestions || [];

  // Extract unique track IDs
  const trackIds = [...new Set(suggestions.map((s) => s.trackId))];

  // Fetch track details from Deezer (batch lookup)
  const {
    data: tracksResponse,
    isLoading: isLoadingTracks,
  } = useApiQuery<ISpotifyTracksResponse>({
    queryKey: ["musicTracks", trackIds],
    endpoint: `${musicEndpoints.getTracks()}?ids=${trackIds.join(",")}`,
    enabled: trackIds.length > 0,
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  const tracks = tracksResponse?.tracks || [];

  // Merge suggestions with track details
  const enrichedSuggestions: IMusicSuggestion[] = suggestions.map((suggestion) => {
    const track = tracks.find((t) => t.id === suggestion.trackId);
    return {
      ...suggestion,
      track,
    };
  });

  // Delete suggestion
  const handleDelete = useCallback(
    async (suggestionId: number) => {
      try {
        await deleteMutation.mutateAsync({
          endpoint: musicEndpoints.deleteSuggestion(suggestionId),
          method: "delete",
          disableSuccessToast: true,
        });

        // Invalidate suggestions query to refresh the list
        queryClient.invalidateQueries({ queryKey: ["musicSuggestions", eventId] });

        getSuccessToast("Предложението е премахнато успешно!");
      } catch (error: any) {
        getErrorToast(error?.response?.data?.message || "Грешка при премахване на предложението");
      }
    },
    [deleteMutation, queryClient, eventId]
  );

  // Toggle upvote
  const handleUpvote = useCallback(
    async (suggestionId: number) => {
      try {
        const response = (await upvoteMutation.mutateAsync({
          endpoint: musicEndpoints.toggleUpvote(suggestionId),
          method: "post",
          disableSuccessToast: true,
        })) as IUpvoteResponse;

        // Invalidate suggestions query to refresh upvote counts
        queryClient.invalidateQueries({ queryKey: ["musicSuggestions", eventId] });

        const message = response.upvoted ? "Upvote добавен!" : "Upvote премахнат!";
        getSuccessToast(message);
      } catch (error: any) {
        getErrorToast(error?.response?.data?.message || "Грешка при upvote");
      }
    },
    [upvoteMutation, queryClient, eventId]
  );

  return {
    suggestions: enrichedSuggestions,
    // Only consider loading if suggestions are loading, or if we have suggestions and tracks are loading
    isLoading: isLoadingSuggestions || (trackIds.length > 0 && isLoadingTracks),
    error: suggestionsError,
    eventEndDateTime: suggestionsResponse?.eventEndDateTime,
    allowMusicSuggestions: suggestionsResponse?.allowMusicSuggestions ?? true,
    handleDelete,
    handleUpvote,
    isDeleting: deleteMutation.isLoading,
    isUpvoting: upvoteMutation.isLoading,
  };
};
